import { useEffect, useRef, useState } from "react";
import { Btn, Dropdown, FilterToolbar, Icon, IconBtn, PageState, SegmentedControl, Spinner, T } from "@v0y4ge/jrni-ui";
import { useNav } from "../../shell/router";
import { can } from "../../shell/access";
import { Screen } from "../../shell/chrome";
import { useIsMobile } from "../../shell/useResponsive";
import { EventsInlineTable, EVENT_STATUS } from "./EventsInlineTable";
import { EventsInlineCalendar } from "./EventsInlineCalendar";
import { useEvents } from "./useEvents";
import { useViewMode, ViewToggle, useRowSelection, BulkActionBar, CARD_GRID, tabExport, eventsSectionTabs } from "../../shell/list-composites";
import { useEventSectionCounts } from "./eventSectionCounts";
import { useFolderLibrary, FolderShellFrame, FolderShellBars, FolderShellOverlays } from "../shared/library-shell";
import type { UIEvent } from "../../lib/events-adapter";
import type { RailView } from "../shared/collections-ui";
import { useT, t as i18nT } from "../../lib/i18n";
import { useDateTimeVersion } from "../../lib/datetime";
import { GHOST_CLASS, GHOST_RADIUS, GhostStyles, SkeletonBox } from "../../lib/loading-skeletons";

/**
 * Events list — verbatim port of the prototype's Events page (table view,
 * stat tiles, filters, search, sort). The ONLY change is the data source:
 * `useEvents()` fetches via the typed api-client → adapter; the existing
 * client-side filter/sort/search runs over that array, byte-for-byte.
 *
 * Interim (flagged): the inline Calendar view + the create split-button +
 * the section tabs port next; approvals sort returns 0 until that layer wires.
 */

// Add Event — opens the Journey designer on a NEW Event journey (2026-08-25):
// `?new=event` tells `newjourneys` to pick the Event template and open the
// designer, instead of dropping the user on the journeys list to choose again.
function CreateEventSplitBtn({ label = "Add Event", size = "md", variant = "primary", full = false }: { label?: string; size?: "sm"|"md"|"lg"; variant?: "primary"|"secondary"; full?: boolean }) {
  const nav = useNav();
  return (
    <Btn size={size} variant={variant} icon={<Icon.plus size={size === "sm" ? 12 : 14} />} style={full ? { width: "100%" } : undefined} onClick={() => nav.go("newjourneys", { new: "event" })}>{label}</Btn>
  );
}

// (formatToday removed 2026-07-19 — hand-rolled English-only date violated the
// lib/datetime standing rule and didn't localize; subtitle now uses friendlyDate.)

// ui-config — static per-column default sort-direction map
const HEADER_DEFAULT_DIR: Record<string, "asc" | "desc"> = { title: "asc", when: "asc", location: "asc", host: "asc", registered: "desc", capacity: "desc", status: "asc", approval: "asc", newest: "desc", date: "asc" };
// ui-config — column label map
const COLUMN_LABELS: Record<string, string> = { title: "Event name", when: "Date", location: "Location", host: "Host", approval: "Approval stage" };
// ui-config — sort label map
const SORT_LABELS: Record<string, string> = { date: "Nearest date", registered: "Most registered", capacity: "Capacity %", newest: "Recently added", status: "Status" };
/* STATUS_ORDER deleted 2026-08-19 (§3): a second copy of the status vocabulary the
   table's pill also declared. Both now read EVENT_STATUS. */

/* Table ghost (CC, 2026-10-08): the list card's own geometry, measured off the loaded
   rows: the Select-all strip (40px), then rows of checkbox, 40px tile, three text
   lines, the registration bar, the status pill and Edit + more, at 16px/18px padding
   (~104px a row). The bar and the pill drop at the same card widths the real rows
   drop them. PageState owns role/aria-busy and the announcement; these shapes are
   decorative. */
// ui-config — ghost shape: rows drawn, title-block line boxes [height, bar height, bar width], and the card widths below which the bar / status drop
const TABLE_GHOST_ROWS = 6;
const TABLE_GHOST_LINES: [number, number, string][] = [[24, 15, "70%"], [21, 12, "45%"], [20, 11, "55%"]];
const TABLE_GHOST_BAR_MIN_W = 760;
const TABLE_GHOST_STATUS_MIN_W = 600;
function EventsTableGhost() {
  const [box, setBox] = useState<HTMLDivElement | null>(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    if (!box || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => setW(Math.round(entries[0]?.contentRect.width ?? 0)));
    ro.observe(box);
    return () => ro.disconnect();
  }, [box]);
  const showBar = w === 0 || w >= TABLE_GHOST_BAR_MIN_W;
  const showStatus = w === 0 || w >= TABLE_GHOST_STATUS_MIN_W;
  return (
    <div ref={setBox} className={GHOST_CLASS} style={{ background: T.white, border: `1px solid ${T.n5}`, borderRadius: 14, overflow: "hidden" }}>
      <GhostStyles />
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 18px", borderBottom: `1px solid ${T.n5}` }}>
        <SkeletonBox width={18} height={18} radius={4} />
        <div style={{ height: 19, display: "flex", alignItems: "center" }}><SkeletonBox variant="text" width={60} height={12} /></div>
      </div>
      {Array.from({ length: TABLE_GHOST_ROWS }, (_, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 16, padding: "16px 18px", borderTop: i ? `1px solid ${T.n5}` : "none" }}>
          <SkeletonBox width={18} height={18} radius={4} />
          <SkeletonBox width={40} height={40} radius={8} />
          <div style={{ flex: "1 1 260px", minWidth: 0, display: "flex", flexDirection: "column", gap: 3 }}>
            {TABLE_GHOST_LINES.map(([lineH, barH, barW], k) => (
              <div key={k} style={{ height: lineH, display: "flex", alignItems: "center" }}>
                <SkeletonBox variant="text" width={barW} height={barH} />
              </div>
            ))}
          </div>
          {showBar ? (
            <div style={{ flex: "0 1 220px", minWidth: 140, display: "flex", flexDirection: "column", gap: 6 }}>
              <SkeletonBox height={6} radius="999px" />
              <div style={{ height: 20, display: "flex", alignItems: "center" }}>
                <SkeletonBox variant="text" width={110} height={12} />
              </div>
            </div>
          ) : null}
          {showStatus ? <span style={{ flex: "0 0 auto", width: 112, display: "inline-flex" }}><SkeletonBox width={84} height={25} radius="999px" /></span> : null}
          <div style={{ flex: "0 0 auto", display: "flex", alignItems: "center", gap: 6 }}>
            <SkeletonBox width={47} height={28} radius={GHOST_RADIUS.control} />
            <SkeletonBox width={28} height={28} radius={GHOST_RADIUS.control} />
          </div>
        </div>
      ))}
    </div>
  );
}

/* Calendar ghost (CC, 2026-10-08): the month view's own frame (nav row, weekday
   strip, day cells) in grey while the first read is pending, so the wait reads as
   a calendar loading rather than a line of text. PageState owns role/aria-busy and
   the announcement; these shapes are decorative. */
// ui-config — ghost shape: weeks drawn, and which day cells carry an event-shaped bar
const CAL_GHOST_WEEKS = 5;
const CAL_GHOST_BUSY = new Set([2, 9, 10, 17, 23, 26, 31]);
function EventsCalendarGhost() {
  return (
    <div className={GHOST_CLASS} style={{ display: "flex", flexDirection: "column", gap: 14, minHeight: 570 }}>
      <GhostStyles />
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <SkeletonBox width={36} height={36} radius={8} />
        <SkeletonBox width={68} height={36} radius={8} />
        <SkeletonBox width={36} height={36} radius={8} />
        <SkeletonBox variant="text" width={150} height={18} style={{ margin: "0 6px" }} />
        <SkeletonBox width={190} height={38} radius={9} />
      </div>
      <div style={{ flex: 1, minHeight: 520, background: T.white, border: `1px solid ${T.n5}`, borderRadius: 12, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0,1fr))", borderBottom: `1px solid ${T.n5}` }}>
          {Array.from({ length: 7 }, (_, i) => (
            <div key={i} style={{ padding: "10px 12px", borderLeft: i ? `1px solid ${T.n5}` : "none" }}>
              <SkeletonBox variant="text" width={28} height={12} />
            </div>
          ))}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0,1fr))" }}>
          {Array.from({ length: CAL_GHOST_WEEKS * 7 }, (_, i) => (
            <div key={i} style={{ minHeight: 112, padding: "8px 8px 10px", borderLeft: i % 7 ? `1px solid ${T.n5}` : "none", borderBottom: `1px solid ${T.n5}`, display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
              <SkeletonBox variant="text" width={18} height={14} style={{ marginLeft: 4 }} />
              {CAL_GHOST_BUSY.has(i) ? <SkeletonBox height={22} radius={5} /> : null}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function CalendarFrame({ lib, views, children }: { lib: ReturnType<typeof useFolderLibrary>; views: RailView[]; children: import("react").ReactNode }) {
  if (lib.desktop) return <FolderShellFrame lib={lib} views={views}>{children}</FolderShellFrame>;
  return <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "auto", padding: "16px 16px 20px" }}>{children}</div>;
}

export default function EventsList() {
  const nav = useNav();
  const t = useT(); // i18n pilot (2026-07-19): keys limited to the shipped base catalog — unmatched copy stays literal (REQ-043b)
  const phone = useIsMobile(640); // the toolbar stays one height on desktop; a phone keeps the 34px segment tap targets
  useDateTimeVersion(); // subtitle date re-formats live on locale/tz change
  const { events: EVENTS, total: serverTotal, loading, error, reload, refreshing } = useEvents();
  // Real section-tab totals (templates/tickets/landing) — the shared hook reads
  // the op-backed stores (listEventTemplates / listTicketTypes / listLandingPages),
  // same counts every event-section screen shows.
  const sectionCounts = useEventSectionCounts();
  const [view, setView] = useState<"table" | "calendar">("table");
  const [filter, setFilter] = useState("all");
  const [locFilter, setLocFilter] = useState<string | null>(null);
  const [hostFilter, setHostFilter] = useState<string | null>(null);
  // Persistence is the toolbar's job now: `persistKey="events.list"` writes
  // `jrni.filters.events.list.v1` as `{ query, selected }` and hydrates through these
  // same setters (§6). The old key is not migrated — it held a search string, not work.
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("date");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const onHeaderSort = (key: string) => {
    if (sort === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSort(key); setSortDir(HEADER_DEFAULT_DIR[key] || "asc"); }
  };

  /* Folder organization — the shared Folder Library shell (family "events"):
     mounted, not copied, so the folders UX is identical to Messages/Questions/
     Surveys/Promos. Folders group events by university department (operator,
     2026-08-13); scoping filters the list AHEAD of the status/location/host/
     search filters below. Calendar view is left un-scoped deliberately —
     folders are a list-organization tool. */
  const lib = useFolderLibrary({
    family: "events",
    /* Ended events live in Archive and the list hides them (matches(), below), so they
       must not count toward "All" / "Unfiled" / a folder either: 36 showed beside one row. */
    ids: EVENTS.filter((e) => e.status !== "past").map((e) => e.id),
    canWrite: can("events.write"),
    allLabel: t("events.list.allEvents", "All events"),
    searchAllLabel: t("collections.searchEverywhereEvents", "Search all events"),
    query,
    failTitle: t("events.list.couldnTUpdateEvent", "Couldn't update event"),
    onAfterPurge: () => reload(),
  });
  const poolSet = new Set(lib.pool);
  // A folder pick clears the status view, so VIEWS and FOLDERS never both read as selected.
  const prevScope = useRef(lib.scope);
  useEffect(() => {
    if (lib.scope !== "all") setFilter("all");
    if (prevScope.current !== lib.scope) { prevScope.current = lib.scope; setView("table"); }
  }, [lib.scope]); // ui-local

  const matches = (e: UIEvent) => {
    if (!poolSet.has(e.id)) return false;
    /* Past events belong to Archive (2026-10-02, Greg). The table already hid them, but they
       still counted here, so a search whose only hit was past read "1 of N shown" over an
       empty table with no empty state. Filtering them here keeps count, table, export and
       the "no matches — Clear filters" state in agreement. */
    if (e.status === "past") return false;
    if (filter !== "all" && e.status !== filter) return false;
    if (locFilter && !e.location.toLowerCase().includes(locFilter.toLowerCase())) return false;
    if (hostFilter && e.host !== hostFilter) return false;
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      const hay = `${e.title} ${e.host} ${e.location} ${e.template || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  };

  const filtered = EVENTS.filter(matches);
  const _evTime = (e: UIEvent) => { const t = Date.parse(e.raw?.startsAt || e.date || ""); return Number.isNaN(t) ? Infinity : t; };
  const _cmp = (a: UIEvent, b: UIEvent) => {
    switch (sort) {
      case "title": return (a.title || "").localeCompare(b.title || "");
      case "location": return (a.location || "").localeCompare(b.location || "");
      case "host": return (a.host || "").localeCompare(b.host || "");
      case "when": return _evTime(a) - _evTime(b);
      case "registered": return (a.registered || 0) - (b.registered || 0);
      case "capacity": return ((a.capacity ? a.registered / a.capacity : 0)) - ((b.capacity ? b.registered / b.capacity : 0));
      case "status": return (EVENT_STATUS[a.status]?.order ?? 9) - (EVENT_STATUS[b.status]?.order ?? 9);
      default: return 0;
    }
  };
  const sorted = [...filtered].sort((a, b) => { if (sort === "date") return 0; const base = _cmp(a, b); return sortDir === "desc" ? -base : base; });

  const counts = {
    all: EVENTS.filter((e) => e.status !== "past").length,
    live: EVENTS.filter((e) => e.status === "live").length,
    upcoming: EVENTS.filter((e) => e.status === "upcoming").length,
    draft: EVENTS.filter((e) => e.status === "draft").length,
    canceled: EVENTS.filter((e) => e.status === "canceled").length,
    past: EVENTS.filter((e) => e.status === "past").length,
  };
  /* "of N" counts what this list can show — ended events live in Archive (2026-10-02, Greg).
     When the server has more rows than were fetched, its total is kept, less the ended rows we can see. */
  const listTotal = serverTotal > EVENTS.length ? serverTotal - counts.past : counts.all;
  const hosts = [...new Set(EVENTS.map((e) => e.host))];
  const locs = [...new Set(EVENTS.map((e) => e.location.split(" · ")[0]))];
  const clearFilters = () => { setFilter("all"); setLocFilter(null); setHostFilter(null); setQuery(""); };
  const filtersActive = filter !== "all" || locFilter || hostFilter || query;

  const sectionTabs = eventsSectionTabs(nav, "events", sectionCounts);
  const headerTitle = view === "calendar" ? t("events.list.calendar", "Calendar")
    : lib.scope !== "all" ? lib.scopeName
    : filter === "all" ? t("events.list.allEvents", "All events")
    : t(EVENT_STATUS[filter]?.labelKey ?? "events.list.allEvents", EVENT_STATUS[filter]?.label ?? "All events");

  /* Sectioned left rail (2026-10-02, matches the Tickets tab): VIEWS are the status
     filters (they replaced the status chips); FOLDERS scope the list. Picking one
     resets the other, so exactly one rail row is ever highlighted. ui-local. */
  const pickView = (id: string) => { setFilter(id); lib.setScope("all"); setView("table"); }; // ui-local — the calendar is unfiltered, so a rail pick returns to the table
  const views: RailView[] = ([
    ["all", t("events.list.allEvents", "All events"), counts.all],
    ["live", t("events.status.live", "Live"), counts.live],
    ["upcoming", t("events.status.upcoming", "Upcoming"), counts.upcoming],
    ["draft", t("events.status.draft", "Draft"), counts.draft],
    ["canceled", t("events.status.cancelled", "Cancelled"), counts.canceled],
  ] as const).map(([id, label, n]) => ({ id, label, count: n, active: lib.scope === "all" && filter === id, onClick: () => pickView(id) }));


  return (
    <Screen rail="events" tabs={sectionTabs} activeTab="events">
      {/* Tickets-style header bar (2026-10-02): white, full width, the active view's
          name as the title, one plain sentence under it, actions on the right. */}
      <header style={{ flex: "0 0 auto", display: "flex", alignItems: "center", gap: 14, padding: "10px 16px", background: T.white, borderBottom: `1px solid ${T.n5}`, boxShadow: "0 1px 2px rgba(51,51,51,0.06)", zIndex: 5, flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 240px", minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{headerTitle}</div>
          <div style={{ fontSize: 12.5, color: T.soft, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t("events.list.headerSub", "Everything live, upcoming and in draft. Events move to Archive once they've ended.")}</div>
        </div>
        {/* The actions wrap on a phone (CC, 2026-10-04): at 390 px "Add Event" was pushed off screen. */}
        <div style={{ flex: "0 1 auto", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {/* The icon SPINS while the refresh runs and goes inert until it
              finishes (2026-09-17, PP) — the request always went out, but
              nothing on screen moved, least of all when the server returned
              the same rows, so the control read as dead. */}
          <IconBtn label={t("events.list.refreshEvents", "Refresh events")} disabled={refreshing} onClick={() => { void reload(); }}>
            {refreshing ? <Spinner size="sm" label="" /> : <Icon.refresh size={16} />}
          </IconBtn>
          <SegmentedControl<"table" | "calendar">
            size={phone ? "md" : "sm"}
            ariaLabel={t("events.list.view", "View")}
            value={view}
            onChange={setView}
            options={[
              { value: "table", label: t("events.list.table", "Table") },
              { value: "calendar", label: t("events.list.calendar", "Calendar") },
            ]}
          />
          {can("reports.export") ? <Btn variant="secondary" icon={<Icon.download size={14} />} onClick={() => tabExport("events", sorted.map((e) => ({ title: e.title, status: e.status, date: e.date, time: e.time, location: e.location, registered: e.registered, capacity: e.capacity })))}>{t("common.actions.export", "Export")}</Btn> : null}
          {can("events.write") ? <CreateEventSplitBtn label={t("events.list.addEvent", "Add Event")} /> : null}
        </div>
      </header>

      {view === "calendar" ? (
        /* Calendar (2026-10-02): My Calendar's look, ALL events — the rail and search do
           not filter it (operator ruling). The rail stays visible; picking from it
           returns to the table. */
        /* Below desktop the shell shows folder cards, not a rail — they don't filter the
           calendar, so the calendar renders on its own there (verifier, 2026-10-02). */
        <CalendarFrame lib={lib} views={views.map((v) => ({ ...v, active: false }))}>
          {loading || error ? (
            <PageState noun={t("events.sectionState.nounEvents", "events")} icon={<Icon.tags size={20} />}
              loading={loading} error={error} onRetry={reload} skeleton={<EventsCalendarGhost />}
              empty={false} filtered={false} onClearFilters={clearFilters}
              action={can("events.write") ? <CreateEventSplitBtn label={t("events.list.addEvent", "Add Event")} /> : null} />
          ) : (
            <EventsInlineCalendar events={EVENTS} fill />
          )}
        </CalendarFrame>
      ) : (
        <FolderShellFrame lib={lib} views={views}>
          {/* Stat tiles removed (2026-10-02, Tickets-tab alignment): they repeated the
              status chips' counts one row above them. The chips are the one count surface. */}
          {/* ONE filter surface (§4). It renders ABOVE the state branch, never inside
              it: losing the filter UI in the one state where the user most needs to
              change a filter is the most common bug in a list screen. Counting stays
              client-side and stays visibly wrong past page one — the toolbar does no
              counting; see the server-aggregates drain row. */}
          <FilterToolbar
            testid="events-filters"
            persistKey="events.list"
            density="compact"
            query={query}
            onQueryChange={setQuery}
            searchPlaceholder={t("events.list.searchPlaceholder", "Search title, host, location…")}
            searchAriaLabel={t("events.list.searchEvents", "Search events")}
            resultCount={{ shown: filtered.length, total: listTotal }}
            facets={[
              { id: "location", label: t("events.eventsList.anyLocation", "Any location"), options: locs.map((l) => ({ value: l, label: l })) },
              { id: "host", label: t("events.eventsList.anyHost", "Any host"), options: hosts.map((h) => ({ value: h, label: h })) },
            ]}
            selected={{ location: locFilter ?? undefined, host: hostFilter ?? undefined }}
            onToggle={(facetId, value) => {
              if (facetId === "location") setLocFilter((prev) => (prev === value ? null : value));
              else setHostFilter((prev) => (prev === value ? null : value));
            }}
            onClearFacet={(facetId) => { if (facetId === "location") setLocFilter(null); else setHostFilter(null); }}
            onClearAll={clearFilters}
            /* Status chips show only without the rail (phone/tablet) — on desktop the
               rail's VIEWS group is the status filter (2026-10-02). */
            leading={lib.desktop ? undefined :
              <SegmentedControl<string>
                size="sm"
                ariaLabel={t("events.list.statusFilter", "Status filter")}
                value={filter}
                onChange={setFilter}
                options={[
                  { value: "all", label: i18nT("events.list.allCountsAll.icu", "All · {value0}", { value0: counts.all }) },
                  { value: "live", label: t("events.list.tEventsStatusLiveCountsLive.icu", "{value0} · {value1}", { value0: t("events.status.live", "Live"), value1: counts.live }) },
                  { value: "upcoming", label: t("events.list.tEventsStatusUpcomingCountsUpcoming.icu", "{value0} · {value1}", { value0: t("events.status.upcoming", "Upcoming"), value1: counts.upcoming }) },
                  { value: "draft", label: i18nT("events.list.draftsCountsDraft.icu", "Drafts · {value0}", { value0: counts.draft }) },
                  { value: "canceled", label: i18nT("events.list.cancelledCountsCanceled.icu", "Cancelled · {value0}", { value0: counts.canceled }) },
                ]}
              />
            }
            actions={
              /* Sort is NOT a filter: it sits outside the facet group, in a real
                 Dropdown, and does not count toward "filters active". */
              <Dropdown<string>
                size="sm"
                aria-label={t("events.list.sortBy", "Sort by")}
                value={sort}
                onChange={(key) => { setSort(key); setSortDir(HEADER_DEFAULT_DIR[key] || "asc"); }}
                options={Object.keys(SORT_LABELS).map((k) => ({ value: k, label: SORT_LABELS[k] }))}
              />
            }
          />
          <FolderShellBars lib={lib} />
          {/* Info banner removed (2026-10-02): permanent tutorial copy above every list;
              Archive is a section tab one click away. */}
          {loading || error || filtered.length === 0 ? (
            <div style={{ marginTop: 14 }}>
              <PageState noun={t("events.sectionState.nounEvents", "events")} icon={<Icon.tags size={20} />}
                loading={loading} error={error} onRetry={reload} skeleton={<EventsTableGhost />}
                empty={filtered.length === 0} filtered={!!filtersActive || lib.scope !== "all"}
                onClearFilters={() => { clearFilters(); lib.setScope("all"); }}
                action={can("events.write") ? <CreateEventSplitBtn label={t("events.list.addEvent", "Add Event")} /> : null} />
            </div>
          ) : (
            <div style={{ marginTop: 14 }}>
              <EventsInlineTable events={sorted.filter((e) => e.status !== "past")} sortKey={sort} sortDir={sortDir} onSort={onHeaderSort}
                folderChipsFor={lib.folderChips}
                onBulkFolderMove={can("events.write") ? (ids, at) => { lib.setSel(new Set(ids)); lib._setMoveAt(at); } : undefined}
                foldersItemFor={can("events.write") ? lib.foldersMenuItem : undefined} />
            </div>
          )}
        </FolderShellFrame>
      )}
      <FolderShellOverlays lib={lib} />
    </Screen>
  );
}
